import { ReactElement } from "react";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";

import toast from "components/ui/toast/Toast";
import { parseCssColorToHex } from "./cssColorParser";
import {
  StotlesBlock,
  StotlesBlockNoteEditor,
  StotlesDOCXExporter,
  StotlesPartialBlock,
  StotlesPDFExporter,
} from "./types";

export interface ExportHeaderContent {
  title?: string;
  description?: string;
}

export function isBlockNoteEmpty(blocks: StotlesPartialBlock[]): boolean {
  if (!blocks.length) return true;

  if (blocks.length > 1) return false;

  const firstBlock = blocks[0];
  if (firstBlock.type !== "paragraph") return false;

  const content = firstBlock.content;
  if (!content) return true;

  if (typeof content === "string") return content === "";

  if (Array.isArray(content)) {
    return (
      content.length === 0 ||
      (content.length === 1 &&
        typeof content[0] === "object" &&
        "type" in content[0] &&
        content[0].type === "text" &&
        "text" in content[0] &&
        content[0].text === "")
    );
  }

  return false;
}

interface TextInline {
  type: "text";
  text: string;
}

interface LinkInline {
  type: "link";
  content: InlineContent[];
}

type InlineContent = TextInline | LinkInline | { type: string };

interface TableCell {
  content: InlineContent[];
}

interface TableRow {
  cells: TableCell[];
}

interface TableContent {
  rows?: TableRow[];
}

type ExtractableContent = string | InlineContent[] | TableContent | undefined;

function mapBlocksForExport(blocks: StotlesBlock[]): StotlesBlock[] {
  const mappedBlocks: StotlesBlock[] = [];

  for (const block of blocks) {
    const mappedChildren = block.children?.length ? mapBlocksForExport(block.children) : [];
    switch (block.type) {
      case "paragraph":
      case "heading":
      case "quote":
      case "codeBlock":
      case "toggleListItem":
      case "bulletListItem":
      case "numberedListItem":
      case "checkListItem":
      case "table":
      case "file":
      case "image":
      case "video":
      case "audio":
      case "stotlesCallout":
        mappedBlocks.push({
          ...block,
          children: mappedChildren,
        } as StotlesBlock);
        break;
      default:
        toast.warning(
          "Unsupported block type found during export. This block will be skipped in the exported document.",
        );
        console.warn("Unsupported block type during export", block);
        break;
    }
  }

  return mappedBlocks;
}

const BLOCKNOTE_PALETTE_COLORS: ReadonlySet<string> = new Set([
  "default",
  "gray",
  "brown",
  "red",
  "orange",
  "yellow",
  "green",
  "blue",
  "purple",
  "pink",
]);

/**
 * Whether a colour value can be safely passed to the DOCX/PDF exporter — either
 * a named palette key, or a CSS colour the `colorsWithCssFallback` proxy can
 * resolve into a hex pair. Values that fail this check (named CSS colours,
 * `hsl(...)`, garbage, etc.) need to be coerced to "default" before export.
 */
function isExporterSafeColor(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (BLOCKNOTE_PALETTE_COLORS.has(value)) return true;
  return parseCssColorToHex(value) !== null;
}

/**
 * Same as `isExporterSafeColor`, but additionally treats pure white as if it
 * were unset. White-on-white is almost always a paste artefact (Word/Notion
 * serialises the page background into every block) and emitting an explicit
 * white shading produces XML noise without any user-visible benefit.
 */
function isMeaningfulBackgroundColor(value: unknown): value is string {
  if (!isExporterSafeColor(value)) return false;
  return parseCssColorToHex(value) !== "#FFFFFF";
}

interface InlineWithStylesAndContent {
  styles?: Record<string, unknown>;
  content?: unknown;
  [key: string]: unknown;
}

function sanitizeInlineContent(content: unknown): unknown {
  if (!Array.isArray(content)) return content;

  return content.map((item) => {
    if (!item || typeof item !== "object") return item;
    let next = item as InlineWithStylesAndContent;

    if (next.styles && typeof next.styles === "object") {
      const cleaned: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(next.styles)) {
        if (key === "backgroundColor" && !isMeaningfulBackgroundColor(value)) {
          continue;
        }
        if (key === "textColor" && !isExporterSafeColor(value)) {
          continue;
        }
        cleaned[key] = value;
      }
      next = { ...next, styles: cleaned };
    }

    if (Array.isArray(next.content)) {
      next = { ...next, content: sanitizeInlineContent(next.content) };
    }

    return next;
  });
}

interface TableContentShape {
  rows?: Array<{
    cells?: Array<{ content?: unknown; [key: string]: unknown }>;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

function sanitizeTableContent(content: TableContentShape): TableContentShape {
  if (!Array.isArray(content.rows)) return content;
  return {
    ...content,
    rows: content.rows.map((row) => ({
      ...row,
      cells: row.cells?.map((cell) => ({
        ...cell,
        content: sanitizeInlineContent(cell.content),
      })),
    })),
  };
}

/**
 * Last-line defence against the DOCX/PDF exporters crashing on unrecognised
 * colour values. Palette names and parseable CSS colours (`rgb(...)`,
 * `#RRGGBB`, `#RGB`) pass through unchanged — those are resolved by the
 * `colorsWithCssFallback` proxy at export time. Anything else (hsl, named CSS
 * colours, garbage from pasted content) is coerced to "default" at the block
 * level and stripped from inline styles, since the exporter would otherwise
 * crash on the missing palette key.
 */
export function sanitizeBlockColors<T extends StotlesPartialBlock>(blocks: T[]): T[] {
  return blocks.map((block) => {
    const next: Record<string, unknown> = { ...block };

    if (next.props && typeof next.props === "object") {
      const props = next.props as Record<string, unknown>;
      const cleanedProps: Record<string, unknown> = { ...props };
      if ("backgroundColor" in props && !isMeaningfulBackgroundColor(props.backgroundColor)) {
        cleanedProps.backgroundColor = "default";
      }
      if ("textColor" in props && !isExporterSafeColor(props.textColor)) {
        cleanedProps.textColor = "default";
      }
      next.props = cleanedProps;
    }

    if (next.content) {
      if (block.type === "table") {
        if (Array.isArray(next.content)) {
          next.content = sanitizeTableContent({
            rows: next.content as TableContentShape["rows"],
          }).rows;
        } else if (typeof next.content === "object") {
          next.content = sanitizeTableContent(next.content as TableContentShape);
        }
      } else if (Array.isArray(next.content)) {
        next.content = sanitizeInlineContent(next.content);
      }
    }

    if (Array.isArray(next.children)) {
      next.children = sanitizeBlockColors(next.children as StotlesPartialBlock[]);
    }

    return next as T;
  });
}

function isTableContent(content: ExtractableContent): content is TableContent {
  return (
    typeof content === "object" &&
    "rows" in content &&
    Array.isArray((content as TableContent).rows)
  );
}

function extractTextFromContent(content: ExtractableContent): string {
  if (!content) return "";

  if (typeof content === "string") return content;

  if (Array.isArray(content)) {
    return content
      .map((inline) => {
        if (inline.type === "text") {
          return (inline as TextInline).text || "";
        }
        if (inline.type === "link") {
          return extractTextFromContent((inline as LinkInline).content);
        }
        return "";
      })
      .join("");
  }

  if (isTableContent(content)) {
    const rows = content.rows || [];
    return rows
      .map((row) => (row.cells || []).map((cell) => extractTextFromContent(cell.content)).join(" "))
      .join("\n");
  }

  return "";
}

function extractPlainText(blocks: StotlesBlock[]): string {
  try {
    let text = "";

    for (const block of blocks) {
      if (block.type === "table" && block.content) {
        const tableContent = block.content as TableContent | TableRow[];
        const rows = Array.isArray(tableContent) ? tableContent : tableContent.rows || [];

        rows.forEach((row) => {
          if (row && row.cells) {
            row.cells.forEach((cell) => {
              text += extractTextFromContent(cell.content) + " ";
            });
            text += "\n";
          }
        });
      } else {
        text += extractTextFromContent(block.content || []);
        text += "\n";
      }

      if (block.children) {
        text += extractPlainText(block.children);
      }
    }

    return text.trim();
  } catch (e) {
    // Make sure unexpected blocks do not crash the app
    Sentry.captureException(e);
    return "";
  }
}

function countWords(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function countCharacters(text: string): number {
  return text.replace(/\s/g, "").length;
}

export function getWordCount(blocks: StotlesBlock[]): number {
  const text = extractPlainText(blocks);
  return countWords(text);
}

export function getCharacterCount(blocks: StotlesBlock[]): number {
  const text = extractPlainText(blocks);
  return countCharacters(text);
}

export function getWordAndCharacterCount(blocks: StotlesBlock[]): {
  words: number;
  characters: number;
} {
  const text = extractPlainText(blocks);
  return {
    words: countWords(text),
    characters: countCharacters(text),
  };
}

/**
 * Validates and safely parses BlockNote initial content from a JSON string
 * @param responseData - The response string from the database (could be JSON or null/undefined)
 * @returns Valid PartialBlock[] array or undefined if invalid/empty
 */
export function validateInitialContent(
  responseData?: string | null,
): StotlesPartialBlock[] | undefined {
  try {
    // Handle null, undefined, or empty string
    if (!responseData || responseData.trim() === "") {
      return undefined;
    }

    // Handle empty array string
    if (responseData.trim() === "[]") {
      return undefined;
    }

    // Try to parse JSON
    let parsed: unknown;
    try {
      parsed = JSON.parse(responseData);
    } catch (parseError) {
      Sentry.captureException(new Error(`Failed to parse BlockNote content: ${parseError}`), {
        extra: { responseData: responseData.substring(0, 200) }, // Log first 200 chars for debugging
      });
      return undefined;
    }

    // Validate it's an array
    if (!Array.isArray(parsed)) {
      Sentry.captureException(new Error("BlockNote content is not an array"), {
        extra: { parsedType: typeof parsed },
      });
      return undefined;
    }

    // Validate it's not empty
    if (parsed.length === 0) {
      return undefined;
    }

    // Basic validation that blocks have required structure
    const isValidBlockStructure = parsed.every((block) => {
      return (
        typeof block === "object" &&
        block !== null &&
        "type" in block &&
        typeof block.type === "string"
      );
    });

    if (!isValidBlockStructure) {
      Sentry.captureException(new Error("BlockNote content has invalid block structure"));
      return undefined;
    }

    // Return the validated blocks
    return parsed as StotlesPartialBlock[];
  } catch (error) {
    // Catch any unexpected errors
    Sentry.captureException(error, {
      tags: { context: "validateInitialContent" },
      extra: { responseData: responseData?.substring(0, 200) },
    });
    return undefined;
  }
}

/**
 * Creates a document with header blocks prepended to the original content
 * @param originalDocument - The original BlockNote document blocks
 * @param headerContent - Optional header content to prepend
 * @returns Modified document with header blocks or original document if no header content
 */
function createDocumentWithHeader(
  originalDocument: StotlesBlock[],
  headerContent?: ExportHeaderContent,
): StotlesBlock[] {
  if (!headerContent || !headerContent.title || !headerContent.description) {
    return originalDocument;
  }

  const headerBlocks: StotlesBlock[] = [];

  // Add title block
  headerBlocks.push({
    id: uuidv4(),
    type: "heading",
    props: {
      level: 1,
      isToggleable: false,
      backgroundColor: "default" as const,
      textColor: "default" as const,
      textAlignment: "left" as const,
    },
    content: [{ type: "text", text: headerContent.title, styles: {} }],
    children: [],
  });

  // Add description block
  headerBlocks.push({
    id: uuidv4(),
    type: "paragraph",
    props: {
      backgroundColor: "default" as const,
      textColor: "default" as const,
      textAlignment: "left" as const,
    },
    content: [{ type: "text", text: headerContent.description, styles: {} }],
    children: [],
  });

  // Add a separator line
  headerBlocks.push({
    id: uuidv4(),
    type: "paragraph",
    props: {
      backgroundColor: "default" as const,
      textColor: "default" as const,
      textAlignment: "center" as const,
    },
    content: [
      {
        type: "text",
        text: "___________________________________________________________________",
        styles: {},
      },
    ],
    children: [],
  });

  // Add empty paragraph for spacing
  headerBlocks.push({
    id: uuidv4(),
    type: "paragraph",
    props: {
      backgroundColor: "default" as const,
      textColor: "default" as const,
      textAlignment: "left" as const,
    },
    content: [],
    children: [],
  });

  // Combine header blocks with original document
  return [...headerBlocks, ...originalDocument];
}

export async function getDocxBlob(
  editor: StotlesBlockNoteEditor,
  headerContent?: ExportHeaderContent,
): Promise<Blob> {
  const exporter = StotlesDOCXExporter;
  const mappedDocument = sanitizeBlockColors(mapBlocksForExport(editor.document));
  const documentToExport = createDocumentWithHeader(mappedDocument, headerContent);
  const blob = await exporter.toBlob(documentToExport);
  return blob;
}

export async function getPdfDocument(
  editor: StotlesBlockNoteEditor,
  headerContent?: ExportHeaderContent,
): Promise<ReactElement> {
  const exporter = StotlesPDFExporter;
  const mappedDocument = sanitizeBlockColors(mapBlocksForExport(editor.document));
  const documentToExport = createDocumentWithHeader(mappedDocument, headerContent);
  const document = await exporter.toReactPDFDocument(documentToExport);
  return document;
}

export type ContentPosition = "cursor" | "start" | "end" | "replace";

export async function insertContent(
  editor: StotlesBlockNoteEditor,
  content: string,
  position: ContentPosition = "end",
  onAfterInsert?: () => void,
): Promise<boolean> {
  try {
    const document = editor.document;
    const parsedBlocks = await editor.tryParseMarkdownToBlocks(content);

    switch (position) {
      case "cursor": {
        await editor.pasteMarkdown(content);
        break;
      }
      case "start": {
        const firstBlock = document[0];
        editor.insertBlocks(parsedBlocks, firstBlock, "before");
        break;
      }
      case "end": {
        if (isBlockNoteEmpty(document)) {
          editor.replaceBlocks(document, parsedBlocks);
        } else {
          const lastBlock = document[document.length - 1];
          editor.insertBlocks(parsedBlocks, lastBlock, "after");
        }
        break;
      }
      case "replace": {
        editor.replaceBlocks(document, parsedBlocks);
        break;
      }
    }
    onAfterInsert?.();
    toast.success("Content inserted into editor");
    return true;
  } catch (error) {
    console.error("Failed to insert content:", error);
    Sentry.captureException(error, {
      tags: { feature: "blocknote-insert-content" },
      extra: { contentLength: content?.length },
    });
    toast.error("Failed to insert content into editor");
    return false;
  }
}
